import 'react-app-polyfill/ie11'
import 'react-app-polyfill/stable'
import React from 'react'
import ReactDOM from 'react-dom'
import { Route, Router, Switch } from 'react-router-dom'
import { LocalizationProvider } from '@mui/x-date-pickers'
import 'intersection-observer'
import { AuthContextProvider, authRoutes } from 'packages/core'
import { Loader, NotificationsProvider } from 'packages/eid-ui'
import * as serviceWorker from './serviceWorker'
import { AppContextProvider } from 'appContext'
import AppRoutes from 'pages/AppRoutes'
import './i18n'
import 'packages/eid-ui/globalStyles.css'
import config from 'config'
import { QueryClient, QueryClientProvider } from '@tanstack/react-query'
import { ReactQueryDevtools } from '@tanstack/react-query-devtools'
import { AdapterMoment } from '@mui/x-date-pickers/AdapterMoment'
import 'moment/locale/de'
import 'moment/locale/fr'
import 'moment/locale/es'
import './index.css'
import { ApplicationInsights } from '@microsoft/applicationinsights-web'
import {
    ReactPlugin,
    withAITracking,
} from '@microsoft/applicationinsights-react-js'
import { createBrowserHistory } from 'history'
import CookieChecker from 'packages/core/auth/cookieChecker'

const queryClientOptions = {
    defaultOptions: {
        queries: {
            refetchOnWindowFocus: false,
        },
    },
}

const queryClient = new QueryClient(queryClientOptions)

const history = createBrowserHistory()

let reactPlugin

if (
    config.APP_INSIGHTS_KEY &&
    config.APP_INSIGHTS_KEY !== '#{APP_INSIGHTS_KEY}#'
) {
    reactPlugin = new ReactPlugin()
    const appInsights = new ApplicationInsights({
        config: {
            instrumentationKey: config.APP_INSIGHTS_KEY,
            extensions: [reactPlugin],
            extensionConfig: {
                [reactPlugin.identifier]: { history },
            },
        },
    })
    appInsights.loadAppInsights()
}

const AppWithAI = reactPlugin
    ? withAITracking(reactPlugin, AppRoutes)
    : AppRoutes

const MOUNT_NODE = document.getElementById('root')

const authConfig = {
    settings: {
        authority: config.AUTHORITY_URL,
        client_id: config.CLIENT_ID,
        scope: config.SCOPE,
        response_type: config.RESPONSE_TYPE ?? 'code',
    },
    initialValue: {
        displayName: '',
        currentUserId: '',
    },
    extractAuthData: async (token) => {
        const rawResponse = await fetch(`${config.API_URL}/api/user`, {
            headers: {
                Authorization: `Bearer ${token}`,
            },
        })
        const response = await rawResponse.json()
        return {
            friendlyName: response.displayName,
            currentUserId: response.personId,
        }
    },
}

const MainApp = (
    <React.StrictMode>
        <Router history={history}>
            <LocalizationProvider dateAdapter={AdapterMoment}>
                <QueryClientProvider client={queryClient}>
                    <CookieChecker config={config}>
                        <AuthContextProvider config={authConfig}>
                            <AppContextProvider>
                                <NotificationsProvider>
                                    <Switch>
                                        {authRoutes.map((ar) => (
                                            <Route
                                                key={ar.path}
                                                exact
                                                path={ar.path}
                                                render={(props) => (
                                                    <ar.component
                                                        {...props}
                                                        render={() => (
                                                            <Loader />
                                                        )}
                                                    />
                                                )}
                                            />
                                        ))}
                                        <AppWithAI />
                                    </Switch>
                                </NotificationsProvider>
                            </AppContextProvider>
                        </AuthContextProvider>
                    </CookieChecker>
                    {process.env.NODE_ENV === 'development' && (
                        <ReactQueryDevtools />
                    )}
                </QueryClientProvider>
            </LocalizationProvider>
        </Router>
    </React.StrictMode>
)

ReactDOM.render(MainApp, MOUNT_NODE)

serviceWorker.unregister()
